import React, { useEffect, useState } from "react";
import { RevenuePromotionFetchApi } from "../../ApiCalls/Revenue";

/* ================= TYPE NORMALIZER ================= */
const normalizeType = (type) => {
  if (!type) return "";
  if (type.toLowerCase() === "specialoffer") return "OFFER";
  return type.toUpperCase();
};

const AdminSponsorRevenue = () => {
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPromotionRevenue();
  }, []);

  const fetchPromotionRevenue = async () => {
    try {
      const res = await RevenuePromotionFetchApi();
      setSummary(res?.summary || {});
      setRows(res?.data || []);
    } catch (error) {
      console.error("Promotion revenue error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= FILTER ================= */
  const filteredData =
    typeFilter === "ALL"
      ? rows
      : rows.filter(
          (item) => normalizeType(item.type) === typeFilter
        );

  if (loading) {
    return (
      <div className="p-6 text-gray-500">
        Loading promotion revenue...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-green-700">
          Sponsored / Banner / Offer Revenue
        </h2>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border rounded px-3 py-2 focus:ring-2 focus:ring-green-400"
        >
          <option value="ALL">All Types</option>
          <option value="BANNER">Banner</option>
          <option value="SPONSORED">Sponsored</option>
          <option value="OFFER">Offer</option>
        </select>
      </div>

      {/* ================= SUMMARY CARDS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <SummaryCard
          title="Total Income"
          value={`₹${summary?.total_income || 0}`}
        />
        <SummaryCard
          title="Active Promotions"
          value={summary?.active_promotions || 0}
        />
        <SummaryCard
          title="Revenue Type"
          value={typeFilter}
        />
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-green-50 text-left">
            <tr>
              <th className="p-3 font-semibold">ID</th>
              <th className="p-3 font-semibold">Type</th>
              <th className="p-3 font-semibold">Seller</th>
              <th className="p-3 font-semibold text-center">Subadmin</th>
              <th className="p-3 font-semibold text-right">Amount</th>
              <th className="p-3 font-semibold text-center">Duration</th>
              <th className="p-3 font-semibold text-center">Active Period</th>
              <th className="p-3 font-semibold">Approved By</th>
            </tr>
          </thead>

          <tbody>
            {filteredData.map((item) => (
              <tr
                key={item.id}
                className="border-t hover:bg-gray-50 transition"
              >
                <td className="p-3 font-medium">
                  SP{item.id}
                </td>

                <td className="p-3 text-green-700 font-semibold">
                  {normalizeType(item.type)}
                </td>

                <td className="p-3 capitalize">
                  {item.seller?.name || "-"}
                </td>

                <td className="p-3 text-center text-gray-500">
                  {item.subadmin_email || "-"}
                </td>

                <td className="p-3 text-right font-semibold">
                  ₹{item.amount}
                </td>

                <td className="p-3 text-center">
                  {item.duration_days} Days
                </td>

                <td className="p-3 text-center">
                  {item.active_period?.start_date} →{" "}
                  {item.active_period?.end_date}
                </td>

                <td className="p-3">
                  {item.approved_by}
                </td>
              </tr>
            ))}

            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan="8"
                  className="p-5 text-center text-gray-500"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ================= SUMMARY CARD ================= */
const SummaryCard = ({ title, value }) => (
  <div className="bg-white rounded-lg shadow p-5 h-[90px] flex flex-col justify-between">
    <p className="text-gray-500 text-sm">{title}</p>
    <p className="text-2xl font-bold text-green-700 leading-none">
      {value}
    </p>
  </div>
);

export default AdminSponsorRevenue;
